import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { RegionApiFrom } from '@api-gen/models/region-api-from';
import { CheckLoginDataApiRoaming } from '@api-roaming/models/check-login-data-api-roaming';
import { CheckLoginEmailRequestApiRoaming } from '@api-roaming/models/check-login-email-request-api-roaming';
import { CountryCodeApiRoaming } from '@api-roaming/models/country-code-api-roaming';
import { ErrorResponse } from '@api/models';
import { LoginFormComponent } from '@auth/components/login-form/login-form.component';
import { AppLinksComponent } from '@core/components/app-links/app-links.component';
import { LanguageState } from '@core/localization/language.state';
import { AppFrontSettingsProp } from '@core/models/client.settings.model';
import { SettingsAsObject } from '@core/models/settings.model';
import { getErrorProp } from '@core/operators/start-with-errors';
import { getLoadingProp } from '@core/operators/start-with-loading';
import { StartWithUnit } from '@core/operators/start-with-unit';
import { RouteEntity } from '@core/router/router.entity';
import { AppSettingsService } from '@core/services/app-settings.service';
import { AppState } from '@core/state/app.state';
import { UserState } from '@core/state/user.state';
import { TranslateModule } from '@ngx-translate/core';
import { RxEffects } from '@rx-angular/state/effects';
import { LogoComponent } from '@shared/components/logo/logo.component';
import { OauthProvider } from '@shared/enums/oauth-provider.enum';
import { combineLatest, Observable } from 'rxjs';


/**
 * Auth page
 */
@Component({
    selector: 'app-login-page',
    templateUrl: './login-page.component.html',
    styleUrls: ['./login-page.component.scss'],
    providers: [RxEffects],
    standalone: true,
    imports: [
        AsyncPipe,
        LogoComponent,
        TranslateModule,
        RouterLink,
        AppLinksComponent,
        LoginFormComponent,
    ],
})
export class LoginPageComponent {
    readonly #appState: AppState = inject(AppState);
    readonly #userState: UserState = inject(UserState);
    readonly #effects: RxEffects = inject(RxEffects);
    readonly #languageState: LanguageState = inject(LanguageState);
    readonly #appSettingsService: AppSettingsService = inject(AppSettingsService);

    public readonly pending$: Observable<boolean> = this.#userState.loading$.pipe(
        getLoadingProp(StartWithUnit.SignIn),
    );

    public readonly error$: Observable<ErrorResponse> = this.#userState.errors$.pipe(
        getErrorProp(StartWithUnit.SignIn),
    );

    public readonly pageTitle: string = this.#appSettingsService.getPropDesign(AppFrontSettingsProp.Labels)?.loginPageTitle;
    public readonly loggedIn$: Observable<boolean> = this.#userState.isAuthorized$;
    public readonly countryCodes$: Observable<CountryCodeApiRoaming[]> = this.#appState.countryCodes$;
    public readonly defaultCode$: Observable<CountryCodeApiRoaming> = this.#appState.defaultCountryCode$;
    public readonly settings$: Observable<SettingsAsObject> = this.#appState.settings$;

    public readonly routeEntity: typeof RouteEntity = RouteEntity;

    public signInByPhone(body: CheckLoginDataApiRoaming): void {
        this.#userState.signInByPhone(body);
    }

    public signInByEmail(body: CheckLoginEmailRequestApiRoaming): void {
        this.#userState.signInByEmail(body);
    }

    public signInByGoogle(): void {
        this.#effects.register(
            combineLatest([
                this.#languageState.language$,
                this.#appState.regions$,
            ]),
            ([language, regions]: [string, RegionApiFrom[]]) => {
                this.#userState.oauthLogin(OauthProvider.Google, regions[0]?.id || 1, language);
            },
        );
    }


}
